import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import useUsageLimits from '../hooks/useUsageLimits';
import UpgradeModal from '../components/UpgradeModal';
import AuthModal from '../components/AuthModal';
import { Camera, Scan, Loader, X, Flame, Beef, Wheat, Droplets, ThumbsUp, ThumbsDown, Sparkles, Lock } from 'lucide-react';

const SUPABASE_URL = 'https://ififowdcasczwlydunfj.supabase.co';
const OPENAI_PROXY = `${SUPABASE_URL}/functions/v1/openai-proxy`;

const ScannerPage = () => {
  const { profile, isAuthenticated } = useAuth();
  const { canUse, getLimit, incrementUsage, isPro } = useUsageLimits();
  
  const [mode, setMode] = useState('photo');
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const fileInputRef = useRef(null);

  const scanLimit = getLimit('scanner');
  const canScan = canUse('scanner');

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => { setImagePreview(reader.result); setResult(null); };
      reader.readAsDataURL(file);
    }
  };

  const analyzeImage = async () => {
    if (!imagePreview) return;
    
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    if (!canScan) {
      setShowUpgradeModal(true);
      return;
    }
    
    setIsAnalyzing(true);
    setResult(null);
    incrementUsage('scanner');
    
    try {
      const userGoal = profile?.goal === 'weightloss' ? 'perdre du poids' : 
                       profile?.goal === 'musclegain' ? 'prendre du muscle' : 'rester en bonne santé';
      
      const response = await fetch(OPENAI_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            {
              role: 'system',
              content: `Tu es Louis, nutritionniste expert français. Analyse cette photo de nourriture.

OBJECTIF DE L'UTILISATEUR: ${userGoal}

Réponds UNIQUEMENT avec ce JSON valide:
{
  "name": "Nom du plat/aliment",
  "desc": "Description en une phrase",
  "calories": 350,
  "protein": 20,
  "carbs": 30,
  "fat": 15,
  "healthScore": 7,
  "verdict": "good",
  "verdictText": "Pourquoi c'est bon ou pas",
  "canEat": true,
  "canEatReason": "Explication par rapport à son objectif",
  "tips": ["Un conseil pratique"],
  "alternatives": ["Une alternative si le plat n'est pas idéal"]
}

RÈGLES:
- healthScore: 1-10 (10=parfait pour la santé)
- verdict: "excellent" si 8-10, "good" si 6-7, "moderate" si 4-5, "poor" si 1-3
- Calories RÉALISTES (une pomme=80kcal, un Big Mac=550kcal, une salade=150-400kcal)
- canEat basé sur l'objectif: si perte de poids, éviter les plats trop caloriques`
            },
            {
              role: 'user',
              content: [
                { type: 'text', text: 'Analyse cette photo et donne-moi les infos nutritionnelles.' },
                { type: 'image_url', image_url: { url: imagePreview, detail: 'high' } }
              ]
            }
          ],
          max_tokens: 600
        })
      });

      const data = await response.json();
      
      if (data.error) {
        setResult({ error: true });
        return;
      }
      
      if (data.choices?.[0]) {
        const content = data.choices[0].message.content;
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            setResult(JSON.parse(jsonMatch[0]));
          } catch (e) {
            setResult({ error: true });
          }
        } else {
          setResult({ error: true });
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
      setResult({ error: true });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const reset = () => { 
    setImagePreview(null); 
    setResult(null); 
    if (fileInputRef.current) fileInputRef.current.value = ''; 
  };

  const getVerdictColor = (v) => ({ excellent: '#10b981', good: '#3b82f6', moderate: '#f59e0b', poor: '#ef4444' }[v] || '#6b7280');
  const getVerdictEmoji = (v) => ({ excellent: '🌟', good: '👍', moderate: '⚠️', poor: '❌' }[v] || '❓');
  const getVerdictLabel = (v) => ({ excellent: 'Excellent choix !', good: 'Bon choix', moderate: 'À consommer modérément', poor: 'À éviter' }[v] || 'Inconnu');

  // Si non connecté
  if (!isAuthenticated) {
    return (
      <div className="scanner-page">
        <div className="locked-feature-card">
          <div className="locked-emoji">📸</div>
          <h1>Scanner Nutrition</h1>
          <p>Prenez une photo de votre plat et découvrez instantanément ses calories et macros !</p>
          <div className="locked-benefits">
            <div className="benefit-highlight">🎁 <strong>3 analyses photo par mois OFFERTES</strong></div>
            <div>✓ Analyse instantanée en 3 secondes</div>
            <div>✓ Verdict santé personnalisé selon vos objectifs</div>
            <div>✓ Suggestions d'alternatives plus saines</div>
          </div>
          <button className="primary-btn" onClick={() => setShowAuthModal(true)}>
            <Sparkles size={20} />
            Créer mon compte gratuit
          </button>
          <p className="locked-subtext">Inscription en 30 secondes • Sans engagement • Sans carte bancaire</p>
        </div>
        {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      </div>
    );
  }

  return (
    <div className="scanner-page">
      <div className="page-header">
        <div>
          <h1>📸 Scanner Nutrition</h1>
          <p>Analyse instantanée de vos plats par Louis</p>
          {!isPro && (
            <span className="usage-badge">
              {scanLimit.remaining} analyses restantes ce mois
              {scanLimit.remaining <= 1 && <Lock size={14} />}
            </span>
          )}
        </div>
      </div>

      <div className="scanner-modes">
        <button className={`mode-tab ${mode === 'photo' ? 'active' : ''}`} onClick={() => setMode('photo')}>
          <Camera size={20} /> Photo de plat
        </button>
        <button className={`mode-tab ${mode === 'barcode' ? 'active' : ''}`} onClick={() => setMode('barcode')}>
          <Scan size={20} /> Code-barres
        </button>
      </div>

      <div className="scanner-area">
        {!imagePreview ? (
          <label className="upload-zone">
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageUpload} />
            <div className="upload-content">
              {mode === 'photo' ? (
                <><Camera size={48} /><h3>Prenez une photo de votre plat</h3><p>ou importez depuis votre galerie</p></>
              ) : (
                <><Scan size={48} /><h3>Scannez un code-barres</h3><p>produit alimentaire uniquement</p></>
              )}
            </div>
          </label>
        ) : (
          <div className="preview-container">
            <button className="close-preview" onClick={reset}><X size={20} /></button>
            <img src={imagePreview} alt="Preview" className="image-preview" />
            {!result && !isAnalyzing && (
              <button className="analyze-btn" onClick={analyzeImage} disabled={!canScan}>
                <Sparkles size={20} /> Analyser avec Louis
              </button>
            )}
            {isAnalyzing && (
              <div className="analyzing-overlay">
                <Loader size={40} className="spin" />
                <p>Louis analyse votre image...</p>
              </div>
            )}
          </div>
        )}

        {result && !result.error && (
          <div className="scan-result">
            <div className="result-top">
              <div className="result-info">
                <h2>{result.name}</h2>
                <p>{result.desc}</p>
              </div>
              <div className="health-score" style={{ borderColor: getVerdictColor(result.verdict) }}>
                <span>{result.healthScore}</span>
                <small>/10</small>
              </div>
            </div>

            <div className="verdict-box" style={{ borderLeftColor: getVerdictColor(result.verdict) }}>
              <span className="verdict-emoji">{getVerdictEmoji(result.verdict)}</span>
              <div>
                <strong>{getVerdictLabel(result.verdict)}</strong>
                <p>{result.verdictText}</p>
              </div>
            </div>

            <div className="nutrition-row">
              <div className="nutri-box orange"><Flame size={20} /><span>{result.calories}</span><small>kcal</small></div>
              <div className="nutri-box red"><Beef size={20} /><span>{result.protein}g</span><small>prot.</small></div>
              <div className="nutri-box blue"><Wheat size={20} /><span>{result.carbs}g</span><small>gluc.</small></div>
              <div className="nutri-box purple"><Droplets size={20} /><span>{result.fat}g</span><small>lip.</small></div>
            </div>

            <div className={`can-eat-box ${result.canEat ? 'yes' : 'no'}`}>
              {result.canEat ? <ThumbsUp size={24} /> : <ThumbsDown size={24} />}
              <div>
                <strong>{result.canEat ? 'Vous pouvez en manger !' : 'À éviter si possible'}</strong>
                <p>{result.canEatReason}</p>
              </div>
            </div>

            {result.tips?.length > 0 && (
              <div className="tips-box">
                <h4>💡 Conseils de Louis</h4>
                {result.tips.map((tip, i) => <p key={i}>• {tip}</p>)}
              </div>
            )}

            {result.alternatives?.length > 0 && !result.canEat && (
              <div className="alternatives-box">
                <h4>🔄 Alternatives suggérées</h4>
                {result.alternatives.map((alt, i) => <p key={i}>• {alt}</p>)}
              </div>
            )}

            <button className="primary-btn" onClick={reset} style={{ marginTop: '16px', width: '100%' }}>
              Scanner autre chose
            </button>
          </div>
        )}

        {result?.error && (
          <div className="error-box">
            <p>😅 Impossible d'analyser cette image. Essayez avec une photo plus nette !</p>
            <button onClick={reset} className="primary-btn">Réessayer</button>
          </div>
        )}

        {!imagePreview && (
          <div className="how-it-works">
            <h3>Comment ça marche ?</h3>
            <div className="steps">
              <div className="step"><div className="step-num">1</div><p>Prends une photo</p></div>
              <div className="step"><div className="step-num">2</div><p>Louis analyse</p></div>
              <div className="step"><div className="step-num">3</div><p>Recevez votre verdict</p></div>
            </div>
          </div>
        )}
      </div>
      
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default ScannerPage;
